import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FileTreeItem } from 'src/app/services/models/file-management.models';

export interface FolderSelectDialogData {
  title: string;
  fileToMove: string;
  folders: FileTreeItem[];
  currentPath: string;
  currentTab: 'user' | 'organization' | 'admin';
}

@Component({
    selector: 'bhub-folder-select-dialog',
    templateUrl: './folder-select-dialog.component.html',
    styleUrls: ['./folder-select-dialog.component.scss'],
    standalone: false
})
export class FolderSelectDialogComponent implements OnInit {
  selectedFolder: string = '';
  expandedFolders: Set<string> = new Set();
  flattenedFolders: { path: string; name: string; level: number; hasChildren: boolean; isExpanded: boolean }[] = [];

  constructor(
    public dialogRef: MatDialogRef<FolderSelectDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FolderSelectDialogData
  ) {}

  ngOnInit(): void {
    // Initialize with root folder
    this.selectedFolder = '';
    this.buildFlattenedFolderList();
  }

  private buildFlattenedFolderList(): void {
    this.flattenedFolders = [];

    // Add root folder
    this.flattenedFolders.push({
      path: '',
      name: 'Root',
      level: 0,
      hasChildren: this.data.folders.some(f => f.isFolder),
      isExpanded: this.expandedFolders.has('')
    });

    // Recursively add folders
    this.addFoldersToList(this.data.folders, 1, '');
  }

  private addFoldersToList(items: FileTreeItem[], level: number, parentPath: string): void {
    const folders = items.filter(item => item.isFolder);

    folders.forEach(folder => {
      const isExpanded = this.expandedFolders.has(folder.path);

      // Only show if parent is expanded or it's a root level folder
      if (level === 1 || this.expandedFolders.has(parentPath) || parentPath === '') {
        this.flattenedFolders.push({
          path: folder.path,
          name: folder.name,
          level: level,
          hasChildren: folder.items && folder.items.some(f => f.isFolder),
          isExpanded: isExpanded
        });

        // Add children if this folder is expanded
        if (isExpanded && folder.items) {
          this.addFoldersToList(folder.items, level + 1, folder.path);
        }
      }
    });
  }

  toggleFolder(folderPath: string): void {
    if (this.expandedFolders.has(folderPath)) {
      this.expandedFolders.delete(folderPath);
    } else {
      this.expandedFolders.add(folderPath);
    }
    this.buildFlattenedFolderList();
  }

  selectFolder(path: string): void {
    this.selectedFolder = path;
  }

  isCurrentFolder(path: string): boolean {
    return path === this.data.currentPath;
  }

  getDisplayPath(path: string): string {
    if (!path) return '/';

    // Clean up the path for display
    const parts = path.split('/').filter(p => p);

    // Remove storage area prefix for cleaner display
    if (parts[0] === 'users' && parts.length > 1) {
      return '/' + parts.slice(2).join('/');
    } else if (parts[0] === 'organization' || parts[0] === '_admin') {
      return '/' + parts.slice(1).join('/');
    }

    return '/' + parts.join('/');
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    if (this.selectedFolder === this.data.currentPath) {
      alert('Cannot move file to the same folder');
      return;
    }
    this.dialogRef.close(this.selectedFolder);
  }

  canSelectFolder(path: string): boolean {
    // Cannot select the current folder
    return path !== this.data.currentPath;
  }
}